import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../services';
import { v4 as uuidv4 } from 'uuid';
import { Button, Modal } from 'antd';
import _ from 'lodash';

const shareBotResources = {
    organization: 'ORGANIZATION',
    worldKnowledge: 'WORLD_KNOWLEDGE'
};

const botModalDefault = {
    visible: false,
    file: {}
};

function ChatView({ user, conversationHistory, setConversationHistory, activeBotResource, botAvatar }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [botModal, setBotModal] = useState(botModalDefault);

    const endOfConversationRef = useRef(null);

    // Get user avatar
    const getUserAvatar = (user) => {
        if (user && user.info && user.info.avatar) {
            if (user.info.avatar.resizes && user.info.avatar.resizes.thumbnail) {
                return user.info.avatar.resizes.thumbnail;
            } else if (user.info.avatar.link) {
                return user.info.avatar.link;
            }
        }
        return null;
    };

    useEffect(() => {
        // Check if the last message was from the user and trigger bot response
        if (conversationHistory.length > 0) {
            const lastMessage = conversationHistory[conversationHistory.length - 1];
            if (lastMessage.type === 'user' && !loading) {
                handleBotResponse(lastMessage.text);
            }
        }
    }, [conversationHistory]);

    useEffect(() => {
        if (endOfConversationRef.current) {
            endOfConversationRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [loading, conversationHistory]);

    const handleBotResponse = async (question) => {
        setLoading(true);
        const org_id = String(user.info.active_organization_id);
        const requestData = {
            org_id,
            question,
            conversational_history: conversationHistory.length > 0 ?
                conversationHistory.map(msg => ({
                    type: msg.type,
                    text: msg.text || msg.answer
                })) : undefined
        };

        const request = activeBotResource === shareBotResources.organization
            ? api.audioSynthesizer.questionBot(requestData)
            : api.audioSynthesizer.openAIBot(requestData);

        let tryCount = 0;
        const maxTries = 2;

        const makeRequest = async () => {
            try {
                const res = await request;
                const body = activeBotResource === shareBotResources.organization
                    ? res.data.body[0]
                    : res.data.body && res.data.body[0];

                if (!body) {
                    throw new Error('No response body');
                }

                const newResponse = {
                    type: 'bot',
                    ...body,
                    id: uuidv4(),
                    timestamp: new Date(),
                    avatar: botAvatar
                };

                setConversationHistory(prev => [...prev, newResponse]);
                setLoading(false);
                setError(false);
            } catch (err) {
                if (tryCount < maxTries) {
                    tryCount++;
                    await makeRequest();
                } else {
                    setError(true);
                    setConversationHistory(prev => [...prev, {
                        type: 'bot',
                        text: 'Network Error. Please Try Again!',
                        timestamp: new Date(),
                        avatar: botAvatar
                    }]);
                    setLoading(false);
                }
            }
        };

        await makeRequest();
    };

    const handleOpenReferenceFile = useCallback(() => {
        if (botModal.file && botModal.file.signed_url) {
            window.open(botModal.file.signed_url, '_blank');
        }
    }, [botModal]);

    const handleCloseModal = () => {
        setBotModal(botModalDefault);
    };

    const handleOpenFileReference = (file) => {
        setBotModal({
            visible: true,
            file
        });
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="chat-view">
            {conversationHistory.map((msg, index) => (
                <div
                    key={index}
                    className={`message-container ${msg.type === 'user' ? 'user-message' : 'bot-message'}`}
                >
                    {msg.type === 'bot' && (
                        <div className="avatar-container">
                            <img src={msg.avatar} alt="Bot" className="avatar" />
                        </div>
                    )}

                    <div className="message-content-wrapper">
                        <div className="message-bubble">
                            <div className="message-content">
                                {msg.text || msg.answer}
                            </div>

                            {msg.reference && !_.isEmpty(msg.reference) && (
                                <div className="reference-list">
                                    <span className="reference-title">Reference</span>
                                    <ul>
                                        {Object.keys(msg.reference).map(item => (
                                            <li
                                                key={item}
                                                onClick={() => handleOpenFileReference(msg.reference[item])}
                                            >
                                                [{item}] {msg.reference[item].file_title || msg.reference[item].pdf_name}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className="message-time">{formatTime(msg.timestamp)}</div>
                    </div>

                    {msg.type === 'user' && (
                        <div className="avatar-container">
                            <img
                                src={msg.avatar}
                                alt="User"
                                className="avatar"
                            />
                        </div>
                    )}
                </div>
            ))}

            {loading && (
                <div className="message-container bot-message">
                    <div className="avatar-container">
                        <img src={botAvatar} alt="Bot" className="avatar" />
                    </div>
                    <div className="loading-indicator">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                    </div>
                </div>
            )}

            <Modal
                visible={botModal.visible}
                title={botModal.file.file_title || botModal.file.pdf_name}
                onCancel={handleCloseModal}
                footer={[
                    <Button
                        key="open"
                        type="primary"
                        onClick={handleOpenReferenceFile}
                    >
                        Open File
                    </Button>
                ]}
                width={800}
                centered
            >
                <div className="reference-content-container">
                    <h1>Contexts</h1>
                    {botModal.file.context && botModal.file.context.map((item, index) => (
                        <div key={index} className="context-item">
                            <h3>{index + 1}.</h3>
                            <span>{item}</span>
                        </div>
                    ))}
                </div>
            </Modal>

            <div ref={endOfConversationRef} className="conversation-end"></div>
        </div>
    );
}

export default ChatView;